import React, { useState, useRef, useEffect } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import toast from 'react-hot-toast';
import { useAuth } from '../../context/AuthContext';
import { avatarColor, AvatarIcon } from '../../utils/avatar';
import { getStaff, createStaff, updateStaff, setStaffActive, deleteStaff } from '../../services/adminService';
import { confirmDialog } from '../../components/Common/confirm';

/* Раздел «Сотрудники»: кто заходит в админку и с какими правами.

   Роли описаны в utils/permissions.js — там же матрица, по которой скрываются
   кнопки. Настоящее ограничение стоит на сервере, интерфейс лишь не показывает
   то, чего человек всё равно не сможет сделать. Доступен раздел только
   главному администратору. */

const IcShield = () => (<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.9" strokeLinecap="round" strokeLinejoin="round"><path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z"/></svg>);
const IcPen = () => (<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.9" strokeLinecap="round" strokeLinejoin="round"><path d="M11 4H4a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7"/><path d="M18.5 2.5a2.121 2.121 0 0 1 3 3L12 15l-4 1 1-4 9.5-9.5z"/></svg>);
const IcEye = () => (<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.9" strokeLinecap="round" strokeLinejoin="round"><path d="M1 12s4-7 11-7 11 7 11 7-4 7-11 7-11-7-11-7z"/><circle cx="12" cy="12" r="3"/></svg>);
const IcCheck = () => (<svg viewBox="0 0 24 24" width="14" height="14" fill="none" stroke="currentColor" strokeWidth="2.4" strokeLinecap="round" strokeLinejoin="round"><polyline points="20 6 9 17 4 12"/></svg>);
const IcNo = () => (<svg viewBox="0 0 24 24" width="14" height="14" fill="none" stroke="currentColor" strokeWidth="2.4" strokeLinecap="round" strokeLinejoin="round"><line x1="5" y1="12" x2="19" y2="12"/></svg>);
const IcInfo = () => (<svg viewBox="0 0 24 24" width="11" height="11" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><circle cx="12" cy="12" r="10"/><line x1="12" y1="16" x2="12" y2="12"/><line x1="12" y1="8" x2="12.01" y2="8"/></svg>);
const IcPlus = () => (<svg viewBox="0 0 24 24" width="16" height="16" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round"><line x1="12" y1="5" x2="12" y2="19"/><line x1="5" y1="12" x2="19" y2="12"/></svg>);
const IcChevron = () => (<svg viewBox="0 0 24 24" width="14" height="14" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round"><polyline points="6 9 12 15 18 9"/></svg>);
const IcX = () => (<svg viewBox="0 0 24 24" width="18" height="18" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><line x1="18" y1="6" x2="6" y2="18"/><line x1="6" y1="6" x2="18" y2="18"/></svg>);

const fmtDate = (d) => (d ? new Date(d).toLocaleDateString('ru-RU', { day: '2-digit', month: '2-digit', year: 'numeric' }) : '—');

const ROLES = {
  admin:   { label: 'Главный администратор', chip: 'violet', ic: <IcShield />, sub: 'Полный доступ ко всему' },
  manager: { label: 'Менеджер',              chip: 'blue',   ic: <IcPen />,    sub: 'Ученики, зачисление и доступы' },
  curator: { label: 'Куратор',               chip: 'green',  ic: <IcEye />,    sub: 'Проверка ДЗ и просмотр данных' },
};

const ROLE_CARDS = [
  {
    key: 'admin',
    perms: [
      { t: 'Курсы, уроки, задания, варианты', on: true },
      { t: 'Ученики: добавить, зачислить, архив, удалить', on: true },
      { t: 'Доступы и проверка ДЗ', on: true },
      { t: 'Сотрудники и роли', on: true },
    ],
  },
  {
    key: 'manager',
    perms: [
      { t: 'Ученики: добавить, зачислить, архив', on: true },
      { t: 'Продление доступов', on: true },
      { t: 'Статистика и карточки учеников', on: true },
      { t: 'Редактирование курсов', on: false },
      { t: 'Проверка ДЗ', on: false },
    ],
  },
  {
    key: 'curator',
    perms: [
      { t: 'Проверка ДЗ: ответы учеников', on: true },
      { t: 'Статистика и карточки учеников', on: true },
      { t: 'Просмотр курсов и материалов', on: true },
      { t: 'Изменение чего-либо', on: false },
    ],
  },
];

/* Чип роли с выпадающим выбором (для не-владельца) */
function RoleChip({ member, onChange }) {
  const [open, setOpen] = useState(false);
  const ref = useRef(null);
  useEffect(() => {
    if (!open) return;
    const h = (e) => { if (ref.current && !ref.current.contains(e.target)) setOpen(false); };
    document.addEventListener('mousedown', h);
    return () => document.removeEventListener('mousedown', h);
  }, [open]);

  const r = ROLES[member.role];
  if (member.owner) return <span className={`pg-chip ${r.chip}`}>{r.label}</span>;

  return (
    <div className="role-pick" ref={ref}>
      <button className={`pg-chip ${r.chip} role-pick-btn`} onClick={() => setOpen(o => !o)}>
        {r.label}<IcChevron />
      </button>
      {open && (
        <div className="role-menu">
          {Object.entries(ROLES).filter(([k]) => k !== 'admin').map(([k, v]) => (
            <button key={k} className={`role-menu-item${k === member.role ? ' on' : ''}`} onClick={() => { onChange(k); setOpen(false); }}>
              <span className="role-menu-ic">{v.ic}</span>
              <span><span className="role-menu-name">{v.label}</span><span className="role-menu-sub">{v.sub}</span></span>
              {k === member.role && <span className="role-menu-check"><IcCheck /></span>}
            </button>
          ))}
        </div>
      )}
    </div>
  );
}

export default function AdminStaffPage() {
  const { user, can } = useAuth();
  const manage = can('staff.manage');   // менеджер и куратор состав только смотрят
  const [staff, setStaff] = useState([]);
  const [loading, setLoading] = useState(true);
  const [inviteOpen, setInviteOpen] = useState(false);
  const [saving, setSaving] = useState(false);
  // Приход из «Быстрых действий» в профиле — сразу окно «Добавить сотрудника»
  const location = useLocation();
  const navigate = useNavigate();
  useEffect(() => {
    if (!location.state?.invite) return;
    setInviteOpen(true);
    navigate(location.pathname, { replace: true, state: null });
  }, [location.state]); // eslint-disable-line
  const [form, setForm] = useState({ name: '', email: '', role: 'curator' });
  const [cardId, setCardId] = useState(null);       // открытая карточка сотрудника
  const card = staff.find(m => m.id === cardId) || null;

  const load = () => getStaff()
    .then(r => setStaff((r.data || []).map(m => ({ ...m, owner: m.id === user?.id }))))
    .catch(() => toast.error('Не удалось загрузить список'))
    .finally(() => setLoading(false));
  useEffect(() => { load(); }, []); // eslint-disable-line

  const setRole = async (id, role) => {
    const m = staff.find(x => x.id === id);
    if (!m || m.role === role) return;
    const ok = await confirmDialog({
      title: 'Сменить роль?',
      message: `${m.name || m.email}: «${ROLES[m.role]?.label || m.role}» → «${ROLES[role]?.label || role}». `
        + `Права изменятся сразу, при следующем обновлении страницы человек увидит новый набор разделов.`,
      confirmText: 'Сменить роль',
      danger: role === 'admin',
    });
    if (!ok) return;
    const prev = staff;
    setStaff(s => s.map(m => (m.id === id ? { ...m, role } : m)));   // сразу, чтобы чип не «залипал»
    try {
      await updateStaff(id, { role });
      toast.success('Роль обновлена');
      load();
    } catch (e) {
      setStaff(prev);
      toast.error(e?.response?.data?.message || 'Не удалось сменить роль');
    }
  };

  const toggleActive = async (m) => {
    try {
      await setStaffActive(m.id, !m.isActive);
      toast.success(m.isActive ? 'Доступ отключён' : 'Доступ включён');
      load();
    } catch (e) { toast.error(e?.response?.data?.message || 'Не удалось изменить доступ'); }
  };

  const removeMember = async (m) => {
    const ok = await confirmDialog({
      title: 'Удалить сотрудника?',
      message: `${m.name || m.email} потеряет доступ в админку. Данные учеников это не затронет.`,
      confirmText: 'Удалить',
    });
    if (!ok) return;
    try {
      await deleteStaff(m.id);
      toast.success('Сотрудник удалён');
      load();
    } catch (e) { toast.error(e?.response?.data?.message || 'Не удалось удалить'); }
  };

  const submitInvite = async (e) => {
    e.preventDefault();
    if (!form.email.trim() || saving) return;
    setSaving(true);
    try {
      const r = await createStaff({ name: form.name.trim(), email: form.email.trim(), role: form.role });
      setInviteOpen(false);
      setForm({ name: '', email: '', role: 'curator' });
      load();
      toast.success(`Сотрудник добавлен. Пароль для входа: ${r.data?.tempPassword || '—'}`, { duration: 12000 });
    } catch (e2) {
      toast.error(e2?.response?.data?.message || 'Не удалось добавить');
    } finally { setSaving(false); }
  };

  return (
    <>
      <div style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', gap: 12, flexWrap: 'wrap', marginBottom: 20 }}>
        <div>
          <h1 className="s-page-title" style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
            Сотрудники
          </h1>
          <p className="s-page-sub">Команда платформы, роли и уровни доступа</p>
        </div>
        {manage && <button className="action-btn" onClick={() => setInviteOpen(true)}><IcPlus />Добавить сотрудника</button>}
      </div>

      {/* Роли — пояснение прав */}
      <div className="role-grid">
        {ROLE_CARDS.map(rc => {
          const r = ROLES[rc.key];
          return (
            /* Обводкой выделена роль того, кто смотрит, — сразу видно, что можешь ты */
            <div key={rc.key} className={`role-card${rc.key === user?.role ? ' owner' : ''}`}>
              <div className="role-head">
                <div className={`role-ic chip-${r.chip}`}>{r.ic}</div>
                <div style={{ minWidth: 0 }}>
                  <div className="role-name">{r.label}{rc.key === user?.role && <span className="role-you">вы</span>}</div>
                  <div className="role-sub">{r.sub}</div>
                </div>
              </div>
              {rc.perms.map((p, i) => (
                <div key={i} className={`role-perm${p.on ? '' : ' no'}`}>
                  <span className={`role-perm-ic ${p.on ? 'yes' : 'off'}`}>{p.on ? <IcCheck /> : <IcNo />}</span>
                  {p.t}
                </div>
              ))}
            </div>
          );
        })}
      </div>

      {/* Список сотрудников */}
      <div className="panel">
        <div className="panel-cap">Команда · {staff.length}</div>
        <div>
          {loading && <div className="staff-empty">Загружаем…</div>}
          {!loading && staff.length === 0 && <div className="staff-empty">Пока только вы</div>}
          {staff.map(m => (
            <div key={m.id} className={`staff-row clickable${m.isActive === false ? ' off' : ''}`}
              role="button" tabIndex={0}
              onClick={() => setCardId(m.id)}
              onKeyDown={(e) => { if (e.key === 'Enter') setCardId(m.id); }}>
              <div className="staff-ava" style={m.avatar ? undefined : { background: avatarColor(m.id) }}>
                {m.avatar ? <img src={m.avatar} alt="" style={{ width: '100%', height: '100%', objectFit: 'cover' }} /> : <AvatarIcon id={m.id} size={22} />}
              </div>
              <div className="staff-main">
                <div className="staff-name">
                  {m.name}
                  {m.owner && <span className="staff-tag owner">Это вы</span>}
                  {m.isActive === false && <span className="staff-tag off">Доступ отключён</span>}
                </div>
                <div className="staff-email">
                  {m.email}
                  {/* пароль показываем, пока человек не задал свой — его можно продиктовать */}
                  {m.tempPassword && <> · пароль: <b>{m.tempPassword}</b></>}
                </div>
              </div>
              {manage
                ? <span onClick={(e) => e.stopPropagation()}><RoleChip member={m} onChange={(role) => setRole(m.id, role)} /></span>
                : <span className={`pg-chip ${ROLES[m.role]?.chip || 'violet'}`}>{ROLES[m.role]?.label || m.role}</span>}
              {manage && (
                <>
                  <button
                    className="staff-off"
                    title={m.isActive === false ? 'Включить доступ' : 'Отключить доступ'}
                    disabled={m.owner}
                    onClick={(e) => { e.stopPropagation(); toggleActive(m); }}
                  >{m.isActive === false ? <IcCheck /> : <IcNo />}</button>
                  <button
                    className="staff-del"
                    disabled={m.owner}
                    title={m.owner ? 'Себя удалить нельзя' : 'Убрать из команды'}
                    onClick={(e) => { e.stopPropagation(); removeMember(m); }}
                  ><IcX /></button>
                </>
              )}
            </div>
          ))}
        </div>
      </div>

      {/* Карточка сотрудника: личные данные, как в профиле. Открыта всем ролям —
          состав команды видят все, но только смотрят. */}
      {card && (
        <div className="adm-backdrop" onMouseDown={(e) => { if (e.target === e.currentTarget) setCardId(null); }}>
          <div className="adm-modal" style={{ maxWidth: 440 }}>
            <div className="adm-modal-head">
              <div className="staff-card-ava" style={card.avatar ? undefined : { background: avatarColor(card.id) }}>
                {card.avatar
                  ? <img src={card.avatar} alt="" style={{ width: '100%', height: '100%', objectFit: 'cover' }} />
                  : <AvatarIcon id={card.id} size={26} />}
              </div>
              <div className="adm-head-tt">
                <div className="adm-modal-title">{card.name}</div>
                <div className="adm-modal-desc">{ROLES[card.role]?.sub || ''}</div>
              </div>
              <button className="adm-x" onClick={() => setCardId(null)}><IcX /></button>
            </div>
            <div className="adm-modal-body" style={{ paddingBottom: 22 }}>
              <div className="prof-rows">
                <div className="prof-row"><span className="prof-k">Имя</span><span className="prof-v">{card.name || '—'}</span></div>
                <div className="prof-row"><span className="prof-k">Email</span><span className="prof-v">{card.email}</span></div>
                <div className="prof-row"><span className="prof-k">Роль</span><span className="prof-v">{ROLES[card.role]?.label || card.role}</span></div>
                <div className="prof-row"><span className="prof-k">В команде с</span><span className="prof-v">{fmtDate(card.createdAt)}</span></div>
                <div className="prof-row"><span className="prof-k">Доступ</span>
                  <span className="prof-v">{card.isActive === false ? 'Отключён' : 'Активен'}</span>
                </div>
                {card.tempPassword && (
                  <div className="prof-row"><span className="prof-k">Пароль для входа</span><span className="prof-v">{card.tempPassword}</span></div>
                )}
              </div>
              <div className="staff-card-perms">
                {(ROLE_CARDS.find(rc => rc.key === card.role)?.perms || []).map((p, i) => (
                  <div key={i} className={`role-perm${p.on ? '' : ' no'}`}>
                    <span className={`role-perm-ic${p.on ? '' : ' no'}`}>{p.on ? <IcCheck /> : <IcNo />}</span>
                    {p.t}
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Окно добавления сотрудника */}
      {inviteOpen && (
        <div className="adm-backdrop" onMouseDown={(e) => { if (e.target === e.currentTarget) setInviteOpen(false); }}>
          <div className="adm-modal" style={{ maxWidth: 460 }}>
            <div className="adm-modal-head">
              <div className="adm-head-ic" style={{ background: 'var(--primary-light)', color: 'var(--primary-dark)' }}><IcPlus /></div>
              <div className="adm-head-tt">
                <div className="adm-modal-title">Добавить сотрудника</div>
                <div className="adm-modal-desc">Появится пароль — продиктуйте его человеку</div>
              </div>
              <button className="adm-x" onClick={() => setInviteOpen(false)}><IcX /></button>
            </div>
            <form onSubmit={submitInvite} className="adm-modal-body" style={{ paddingBottom: 22 }}>
              <label className="fld-lbl">Имя</label>
              <input className="fld" value={form.name} onChange={e => setForm(f => ({ ...f, name: e.target.value }))} placeholder="Иван Иванов" autoFocus />
              <label className="fld-lbl">Email</label>
              <input className="fld" type="email" value={form.email} onChange={e => setForm(f => ({ ...f, email: e.target.value }))} placeholder="ivan@platform.ru" />
              <label className="fld-lbl">Роль</label>
              <div className="role-radio">
                {Object.entries(ROLES).map(([k, v]) => (
                  <button type="button" key={k} className={`role-radio-btn${form.role === k ? ' on' : ''}`} onClick={() => setForm(f => ({ ...f, role: k }))}>
                    <span className="role-menu-ic">{v.ic}</span>
                    <span style={{ textAlign: 'left', minWidth: 0 }}><span className="role-menu-name">{v.label}</span><span className="role-menu-sub">{v.sub}</span></span>
                  </button>
                ))}
              </div>
              <div style={{ display: 'flex', gap: 10, marginTop: 18 }}>
                <button type="button" className="action-btn secondary" style={{ flex: 1 }} onClick={() => setInviteOpen(false)}>Отмена</button>
                <button type="submit" className="action-btn" style={{ flex: 1 }} disabled={saving}>{saving ? 'Добавляем…' : 'Добавить'}</button>
              </div>
            </form>
          </div>
        </div>
      )}
    </>
  );
}
